"use client"

import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { ref, push, set } from "../lib/firebase-database"
import { ref as storageRef, uploadBytes, getDownloadURL } from "../lib/firebase-storage"
import { db, storage } from "../firebase"
import { useAuth } from "../hooks/useAuth"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Textarea } from "./ui/textarea"
import { Alert, AlertDescription, AlertTitle } from "./ui/alert"
import { MaterialIcon } from "./MaterialIcon"

const facilityOptions = [
  { id: "wifi", label: "WiFi", icon: () => <MaterialIcon name="wifi" /> },
  { id: "tv", label: "TV", icon: () => <MaterialIcon name="tv" /> },
  { id: "ac", label: "AC", icon: () => <MaterialIcon name="ac_unit" /> },
  { id: "kitchen", label: "Kitchen", icon: () => <MaterialIcon name="kitchen" /> },
  { id: "parking", label: "Parking", icon: () => <MaterialIcon name="local_parking" /> },
  { id: "petFriendly", label: "Pet Friendly", icon: () => <MaterialIcon name="pets" /> },
  { id: "pool", label: "Swimming Pool", icon: () => <MaterialIcon name="pool" /> },
  { id: "gym", label: "Gym", icon: () => <MaterialIcon name="fitness_center" /> },
  { id: "gamesRoom", label: "Games Room", icon: () => <MaterialIcon name="sports_esports" /> },
  { id: "garden", label: "Garden Area", icon: () => <MaterialIcon name="yard" /> },
  { id: "mountainView", label: "Mountain View", icon: () => <MaterialIcon name="landscape" /> },
  { id: "beachView", label: "Beach View", icon: () => <MaterialIcon name="beach_access" /> },
  { id: "alcoholAllowed", label: "Alcohol Allowed", icon: () => <MaterialIcon name="local_bar" /> },
  { id: "noDrinking", label: "No Drinking", icon: () => <MaterialIcon name="no_drinks" /> },
  { id: "birthday", label: "Birthday Celebrations", icon: () => <MaterialIcon name="cake" /> },
  { id: "party", label: "Party Celebrations", icon: () => <MaterialIcon name="celebration" /> },
  { id: "indoorGames", label: "Indoor Games", icon: () => <MaterialIcon name="sports_kabaddi" /> },
  { id: "bbq", label: "BBQ", icon: () => <MaterialIcon name="outdoor_grill" /> },
  { id: "meditation", label: "Meditation Area", icon: () => <MaterialIcon name="self_improvement" /> },
  { id: "spa", label: "Spa", icon: () => <MaterialIcon name="spa" /> },
  { id: "cottages", label: "Cottages", icon: () => <MaterialIcon name="cottage" /> },
  { id: "bungalow", label: "Bungalow", icon: () => <MaterialIcon name="foundation" /> },
  { id: "kingBed", label: "King Size Bed", icon: () => <MaterialIcon name="king_bed" /> },
  { id: "golfCourse", label: "Golf Course", icon: () => <MaterialIcon name="golf_course" /> },
  { id: "hiking", label: "Hiking", icon: () => <MaterialIcon name="hiking" /> },
  { id: "turf", label: "Turf", icon: () => <MaterialIcon name="sports_cricket" /> },
  { id: "cctv", label: "CCTV", icon: () => <MaterialIcon name="videocam" /> },
  { id: "powerBackup", label: "24/7 Power Backup", icon: () => <MaterialIcon name="bolt" /> },
  { id: "preWedding", label: "Pre-Wedding Shoot", icon: () => <MaterialIcon name="video_camera_front" /> },
  { id: "wedding", label: "Wedding", icon: () => <MaterialIcon name="face_3" /> },
  { id: "bigFunction", label: "Big Function", icon: () => <MaterialIcon name="groups" /> },
  { id: "towels", label: "Towels", icon: () => <MaterialIcon name="dry_cleaning" /> },
  { id: "dinner", label: "Dinner", icon: () => <MaterialIcon name="restaurant" /> },
].map((facility) => ({
  ...facility,
  icon: () => <div className="text-gray-600">{facility.icon()}</div>,
}))

export default function AddPropertyPage() {
  const [formData, setFormData] = useState({
    name: "",
    thumbnail: null,
    mainCity: "",
    address: "",
    description: "",
    architecturalUniqueness: "", // Add this new field
    guestCount: "",
    bedrooms: "",
    rate: "", // Base rate (required)
    pricing: {
      enabled: false,
      categories: [
        { heading: "", price: "" }
      ],
      note: "" // Optional note about pricing
    },
    facilities: [],
    images: [],
    locationMap: "",
    locationHighlights: [],
    management: [{ name: "", image: null }],
    owner: { name: "", image: null },
    youtubeLink: "",
    instagramProfile: "",
    whatsappNumber: "",
    totalVillaArea: "",
    ownerEmail: "",
    upiId: "",
    googleMapsLink: "",
    rules: [],
    trainDistance: "",
    busDistance: "",
    locationImages: [],
    youtubeShorts: [],
    location: {
      lat: null,
      lng: null
    },
    stationPickup: false,
    busStopPickup: false,
    meals: {
      available: false,
      types: [],
      description: "",
      images: [],
    },
    checkInTime: "",
    checkOutTime: "", 
    poolSize: "",
    poolCount: "",
    cancellationPolicy: "", 
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const navigate = useNavigate()
  const { user, isAdmin } = useAuth()
  const { id } = useParams()

  // First, generate a unique villaId when the component mounts
  const [villaId, setVillaId] = useState(() => {
    return `villa_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  });

  const extractCoordinates = async (url) => {
    if (!url) return null;

    try {
      // First try to expand short URL if it's a maps.app.goo.gl link
      if (url.includes('maps.app.goo.gl')) {
        const response = await fetch(url);
        const fullUrl = response.url;
        // Now work with the expanded URL
        url = fullUrl;
      }

      // Try different coordinate extraction patterns
      let coords = null;

      // Pattern 1: Standard maps link with @lat,lng
      let match = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (match) {
        return {
          lat: parseFloat(match[1]),
          lng: parseFloat(match[2])
        };
      }

      // Pattern 2: Place ID URL
      if (url.includes('/place/')) {
        const placeIdMatch = url.match(/place\/([^\/]+)/);
        if (placeIdMatch) {
          const placeName = decodeURIComponent(placeIdMatch[1]);
          // Try to find coordinates after place name
          const coordMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
          if (coordMatch) {
            return {
              lat: parseFloat(coordMatch[1]),
              lng: parseFloat(coordMatch[2])
            };
          }
        }
      }

      // Pattern 3: Maps coordinates in !3d!4d format
      const lat3d = url.match(/!3d(-?\d+\.\d+)/);
      const lng4d = url.match(/!4d(-?\d+\.\d+)/);
      if (lat3d && lng4d) {
        return {
          lat: parseFloat(lat3d[1]),
          lng: parseFloat(lng4d[1])
        };
      }

      // Pattern 4: Query parameter format
      const searchParams = new URL(url).searchParams;
      const queryCoords = searchParams.get('q')?.match(/(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (queryCoords) {
        return {
          lat: parseFloat(queryCoords[1]),
          lng: parseFloat(queryCoords[2])
        };
      }

      return null;
    } catch (error) {
      console.error('Error extracting coordinates:', error);
      return null;
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target

    if (type === "checkbox") {
      if (name === "stationPickup" || name === "busStopPickup") {
        setFormData((prev) => ({
          ...prev,
          [name]: checked
        }));
      } else if (name.startsWith("paid_")) {
        // Handle paid facility checkbox
        const facilityId = name.replace("paid_", "")
        setFormData((prev) => ({
          ...prev,
          facilities: prev.facilities.map((facility) => {
            if (typeof facility === 'string') {
              return facility === facilityId
                ? { id: facilityId, isPaid: checked }
                : { id: facility, isPaid: false }
            }
            return facility.id === facilityId
              ? { ...facility, isPaid: checked }
              : facility
          })
        }))
      } else {
        // Handle facility selection checkbox
        const facilityExists = formData.facilities.some(f =>
          typeof f === 'string' ? f === name : f.id === name
        )
        if (checked && !facilityExists) {
          setFormData((prev) => ({
            ...prev,
            facilities: [...prev.facilities, { id: name, isPaid: false }]
          }))
        } else if (!checked && facilityExists) {
          setFormData((prev) => ({
            ...prev,
            facilities: prev.facilities.filter(f =>
              typeof f === 'string' ? f !== name : f.id !== name
            )
          }))
        }
      }
    } else if (name === "googleMapsLink") {
      setFormData(prev => ({
        ...prev,
        googleMapsLink: value
      }));

      // Extract coordinates asynchronously
      extractCoordinates(value).then(coords => {
        if (coords) {
          setFormData(prev => ({
            ...prev,
            location: coords
          }));
        }
      }).catch(error => {
        console.error('Error handling Maps link:', error);
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  }

  const handleImageUpload = async (e, field) => {
    const files = e.target.files;
    if (!files.length) return;

    const promises = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      let folderPath = "";

      // Organize files in villa-specific folders
      switch (field) {
        case "images":
          folderPath = `villas/${villaId}/property-images`;
          break;
        case "thumbnail":
          folderPath = `villas/${villaId}/thumbnail`;
          break;
        case "locationMap":
          folderPath = `villas/${villaId}/location-maps`;
          break;
        case "meals.images":
          folderPath = `villas/${villaId}/meal-images`;
          break;
        case "locationImages":
          folderPath = `villas/${villaId}/location-images`;
          break;
        case "owner":
          folderPath = `villas/${villaId}/owner`;
          break;
        default:
          if (field.startsWith("management")) {
            folderPath = `villas/${villaId}/management`;
          }
          break;
      }

      const imageRef = storageRef(storage, `${folderPath}/${file.name}`);

      const uploadPromise = uploadBytes(imageRef, file)
        .then(async () => {
          const downloadURL = await getDownloadURL(imageRef);
          return { field, url: downloadURL };
        })
        .catch((error) => {
          console.error("Error uploading image:", error);
          setError(`Failed to upload image: ${error.message}`);
          return null;
        });

      promises.push(uploadPromise);
    }

    try {
      const results = await Promise.all(promises);
      results.forEach((result) => {
        if (!result) return;

        // Update formData based on field type
        if (result.field === "meals.images") {
          setFormData((prev) => ({
            ...prev,
            meals: {
              ...prev.meals,
              images: [...(prev.meals.images || []), result.url],
            },
          }));
        } else if (result.field === "images") {
          setFormData((prev) => ({
            ...prev,
            images: [...prev.images, result.url],
          }))
        } else if (result.field === "owner") {
          setFormData((prev) => ({
            ...prev,
            owner: { ...prev.owner, image: result.url },
          }))
        } else if (result.field.startsWith("management")) {
          const index = Number.parseInt(result.field.split("-")[1])
          setFormData((prev) => ({
            ...prev,
            management: prev.management.map((item, i) => (i === index ? { ...item, image: result.url } : item)),
          }))
        } else {
          setFormData((prev) => ({
            ...prev,
            [result.field]: result.url,
          }))
        }
      })
    } catch (error) {
      console.error("Error processing image uploads:", error)
      setError("Failed to process image uploads.")
    }
  }

  const handleLocationImagesUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;

    const promises = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const imageRef = storageRef(
        storage, 
        `villas/${villaId}/location-images/${Date.now()}_${file.name}`
      );

      const uploadPromise = uploadBytes(imageRef, file)
        .then(async () => {
          const downloadURL = await getDownloadURL(imageRef);
          return downloadURL;
        })
        .catch((error) => {
          console.error("Error uploading location image:", error);
          return null;
        });

      promises.push(uploadPromise);
    }

    try {
      const urls = await Promise.all(promises);
      const validUrls = urls.filter((url) => url !== null);

      setFormData((prev) => ({
        ...prev,
        locationImages: [...(prev.locationImages || []), ...validUrls],
      }));
    } catch (error) {
      console.error("Error processing location images:", error);
      setError("Failed to upload location images.");
    }
  }

  const handleCoordinateChange = (e, type) => {
    const value = Number.parseFloat(e.target.value)
    setFormData((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        [type]: value,
      },
    }))
  }

  const deleteImage = (field, index) => {
    setFormData((prev) => {
      if (field === "images") {
        const newImages = [...prev.images]
        newImages.splice(index, 1)
        return { ...prev, images: newImages }
      } else if (field === "thumbnail") {
        return { ...prev, thumbnail: null }
      } else if (field === "locationMap") {
        return { ...prev, locationMap: null }
      } else if (field === "owner") {
        return { ...prev, owner: { ...prev.owner, image: null } }
      } else if (field.startsWith("management")) {
        const managementIndex = Number.parseInt(field.split("-")[1])
        const newManagement = prev.management.map((item, i) =>
          i === managementIndex ? { ...item, image: null } : item,
        )
        return { ...prev, management: newManagement }
      }
      return prev
    })
  }

  const handleLocationHighlight = (index, value) => {
    const newHighlights = [...formData.locationHighlights]
    newHighlights[index] = value
    setFormData((prev) => ({
      ...prev,
      locationHighlights: newHighlights,
    }))
  }

  const addLocationHighlight = () => {
    setFormData((prev) => ({
      ...prev,
      locationHighlights: [...prev.locationHighlights, ""],
    }))
  }

  const removeLocationHighlight = (index) => {
    setFormData((prev) => ({
      ...prev,
      locationHighlights: prev.locationHighlights.filter((_, i) => i !== index),
    }))
  }

  const handleManagementChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      management: prev.management.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }))
  }

  const addManagementMember = () => {
    setFormData((prev) => ({
      ...prev,
      management: [...prev.management, { name: "", image: null }],
    }))
  }

  const removeManagementMember = (index) => {
    setFormData((prev) => ({
      ...prev,
      management: prev.management.filter((_, i) => i !== index),
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!isAdmin) {
        throw new Error("You do not have permission to add properties.");
      }

      // Check for valid coordinates
      const hasValidCoordinates = 
        formData.location?.lat && 
        formData.location?.lng && 
        !isNaN(Number(formData.location.lat)) && 
        !isNaN(Number(formData.location.lng));

      // If no valid coordinates, try to extract from Maps link
      if (!hasValidCoordinates) {
        const coords = await extractCoordinates(formData.googleMapsLink);
        if (coords) {
          formData.location = coords;
        } else {
          throw new Error(
            "Please provide either valid coordinates (latitude and longitude) or a Google Maps link with location coordinates."
          );
        }
      }

      // Ensure coordinates are numbers
      const cleanFormData = {
        ...formData,
        id: villaId,
        createdAt: Date.now(),
        ownerId: user.uid,
        location: {
          lat: Number(formData.location.lat),
          lng: Number(formData.location.lng)
        }
      };

      // Validate coordinate ranges
      if (
        cleanFormData.location.lat < -90 || 
        cleanFormData.location.lat > 90 || 
        cleanFormData.location.lng < -180 || 
        cleanFormData.location.lng > 180
      ) {
        throw new Error("Invalid coordinate values. Latitude must be between -90 and 90, and longitude between -180 and 180.");
      }

      const villaRef = ref(db, `villas/${villaId}`);
      await set(villaRef, cleanFormData);

      setSuccess(true);
      setTimeout(() => {
        navigate("/admin");
      }, 2000);
    } catch (error) {
      console.error("Error adding property:", error);
      setError(`Failed to add property: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        Access denied. Please log in as an admin.
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-black text-white p-4 sticky top-0 z-10 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          &lt; Back
        </Button>
        <div className="container mx-auto px-4 lg:px-8">
          <h1 className="text-xl font-bold">Add New Property</h1>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto bg-white p-4 sm:p-8 rounded-lg shadow">
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mb-6 bg-green-50 text-green-800 border-green-200">
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>Property added successfully! Redirecting...</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Basic Information</h2>

              <div>
                <Label htmlFor="name">Property Name</Label>
                <Input id="name" name="name" value={formData.name} onChange={handleChange} required />
              </div>

              <div>
                <Label htmlFor="thumbnail">Thumbnail Image</Label>
                <Input
                  id="thumbnail"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, "thumbnail")}
                />
                {formData.thumbnail && (
                  <div className="relative mt-2">
                    <img
                      src={formData.thumbnail || "/placeholder.svg"}
                      alt="Thumbnail"
                      className="mt-2 h-24 w-24 object-cover rounded"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 rounded-full h-6 w-6 p-0"
                      onClick={() => deleteImage("thumbnail")}
                    >
                      ×
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="mainCity">Main City</Label>
                <Input id="mainCity" name="mainCity" value={formData.mainCity} onChange={handleChange} required />
              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Textarea id="address" name="address" value={formData.address} onChange={handleChange} required />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="guestCount">Guest Count</Label>
                  <Input
                    id="guestCount"
                    name="guestCount"
                    type="number"
                    value={formData.guestCount}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="bedrooms">Bedrooms</Label>
                  <Input
                    id="bedrooms"
                    name="bedrooms"
                    type="number"
                    value={formData.bedrooms}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="totalVillaArea">Total Villa Area (m²)</Label>
                  <Input
                    id="totalVillaArea"
                    name="totalVillaArea"
                    type="number"
                    value={formData.totalVillaArea}
                    onChange={handleChange}
                    placeholder="Optional: Enter villa area if known"
                  />
                </div>

                <div>
                  <Label htmlFor="poolCount">Number of Pools</Label>
                  <Input
                    id="poolCount"
                    name="poolCount"
                    type="number"
                    value={formData.poolCount}
                    onChange={handleChange}
                    placeholder="Enter number of pools"
                  />
                </div>

                <div>
                  <Label htmlFor="poolSize">Pool Size (m²)</Label>
                  <Input
                    id="poolSize"
                    name="poolSize"
                    type="number"
                    value={formData.poolSize}
                    onChange={handleChange}
                    placeholder="Enter pool size"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="rate">Rate (per night)</Label>
                <Input id="rate" name="rate" type="number" value={formData.rate} onChange={handleChange} required />
              </div>

              {/* Custom Pricing Categories */}
              <div className="space-y-4 border p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Custom Pricing Categories</h3>
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="enableCustomPricing" className="cursor-pointer">
                      Enable Custom Pricing
                    </Label>
                    <input
                      type="checkbox"
                      id="enableCustomPricing"
                      checked={formData.pricing.enabled}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          pricing: {
                            ...prev.pricing,
                            enabled: e.target.checked,
                          },
                        }))
                      }
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  </div>
                </div>

                {formData.pricing.enabled && (
                  <div className="space-y-4">
                    {formData.pricing.categories.map((category, index) => (
                      <div key={index} className="flex gap-4 items-start">
                        <div className="flex-1">
                          <Label htmlFor={`category-${index}-heading`}>Category</Label>
                          <Input
                            id={`category-${index}-heading`}
                            value={category.heading}
                            onChange={(e) => {
                              const newCategories = [...formData.pricing.categories]
                              newCategories[index].heading = e.target.value
                              setFormData((prev) => ({
                                ...prev,
                                pricing: {
                                  ...prev.pricing,
                                  categories: newCategories
                                }
                              }))
                            }}
                            placeholder="e.g., Saturday (8 persons)"
                          />
                        </div>
                        <div className="w-1/3">
                          <Label htmlFor={`category-${index}-price`}>Price</Label>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">₹</span>
                            <Input
                              id={`category-${index}-price`}
                              type="number"
                              value={category.price}
                              onChange={(e) => {
                                const newCategories = [...formData.pricing.categories]
                                newCategories[index].price = e.target.value
                                setFormData((prev) => ({
                                  ...prev,
                                  pricing: {
                                    ...prev.pricing,
                                    categories: newCategories
                                  }
                                }))
                              }}
                              placeholder="0"
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="mt-8"
                          onClick={() => {
                            const newCategories = formData.pricing.categories.filter((_, i) => i !== index)
                            setFormData((prev) => ({
                              ...prev,
                              pricing: {
                                ...prev.pricing,
                                categories: newCategories
                              }
                            }))
                          }}
                        >
                          ×
                        </Button>
                      </div>
                    ))}

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          pricing: {
                            ...prev.pricing,
                            categories: [...prev.pricing.categories, { heading: "", price: "" }]
                          }
                        }))
                      }}
                    >
                      Add Price Category
                    </Button>

                    <div>
                      <Label htmlFor="pricing-note">Pricing Note (Optional)</Label>
                      <Textarea
                        id="pricing-note"
                        value={formData.pricing.note}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            pricing: {
                              ...prev.pricing,
                              note: e.target.value
                            }
                          }))
                        }}
                        placeholder="Add any additional notes about pricing..."
                        rows={2}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Property Images */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Property Images</h2>

              <div>
                <Label htmlFor="images">Property Images</Label>
                <Input
                  id="images"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleImageUpload(e, "images")}
                />
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {formData.images.map((image, index) => (
                    <div key={index} className="relative">
                      <img
                        src={image || "/placeholder.svg"}
                        alt={`Property ${index + 1}`}
                        className="h-24 w-24 object-cover rounded"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute -top-2 -right-2 rounded-full h-6 w-6 p-0"
                        onClick={() => deleteImage("images", index)}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* About This Place */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">About This Place</h2>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={8}
                  placeholder="Enter property description. Press Enter for new paragraphs."
                  className="whitespace-pre-line"
                />
              </div>

              {/* Add Architectural Uniqueness field */}
              <div>
                <Label htmlFor="architecturalUniqueness">Architectural Uniqueness</Label>
                <Textarea
                  id="architecturalUniqueness"
                  name="architecturalUniqueness"
                  value={formData.architecturalUniqueness || ""}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Describe any unique architectural features (optional)"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Leave blank if not applicable. This section will only appear on the villa detail page if filled.
                </p>
              </div>
            </div>

            {/* Meals Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Meals</h2>

              <div className="space-y-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.meals.available}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        meals: {
                          ...prev.meals,
                          available: e.target.checked,
                        },
                      }))
                    }
                    className="rounded border-gray-300"
                  />
                  <span>Meal Service Available</span>
                </label>

                {formData.meals.available && (
                  <>
                    <div>
                      <Label>Meal Types</Label>
                      <div className="flex flex-wrap gap-4 mt-2">
                        {["veg", "nonveg", "vegan"].map((type) => (
                          <label key={type} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={formData.meals.types.includes(type)}
                              onChange={(e) => {
                                const newTypes = e.target.checked
                                  ? [...formData.meals.types, type]
                                  : formData.meals.types.filter((t) => t !== type)
                                setFormData((prev) => ({
                                  ...prev,
                                  meals: {
                                    ...prev.meals,
                                    types: newTypes,
                                  },
                                }))
                              }}
                              className="rounded border-gray-300"
                            />
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6">
                                {type === "veg" && (
                                  <svg viewBox="0 0 24 24">
                                    <rect
                                      width="20"
                                      height="20"
                                      x="2"
                                      y="2"
                                      fill="none"
                                      stroke="green"
                                      strokeWidth="2"
                                    />
                                    <circle cx="12" cy="12" r="6" fill="green" />
                                  </svg>
                                )}
                                {type === "nonveg" && (
                                  <svg viewBox="0 0 24 24">
                                    <rect width="20" height="20" x="2" y="2" fill="none" stroke="red" strokeWidth="2" />
                                    <circle cx="12" cy="12" r="6" fill="red" />
                                  </svg>
                                )}
                                {type === "vegan" && (
                                  <svg viewBox="0 0 24 24">
                                    <rect
                                      width="20"
                                      height="20"
                                      x="2"
                                      y="2"
                                      fill="none"
                                      stroke="darkgreen"
                                      strokeWidth="2"
                                    />
                                    <path d="M12 6l-6 12h12z" fill="darkgreen" />
                                  </svg>
                                )}
                              </div>
                              <span className="capitalize">{type}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="meal-description">Description</Label>
                      <Textarea
                        id="meal-description"
                        value={formData.meals.description}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            meals: {
                              ...prev.meals,
                              description: e.target.value,
                            },
                          }))
                        }
                        placeholder="Describe your meal service (e.g., We provide healthy home-cooked meals including breakfast, lunch, and dinner)"
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label htmlFor="meal-images">Menu Card Images</Label>
                      <Input
                        id="meal-images"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={async (e) => {
                          const files = Array.from(e.target.files)
                          const promises = files.map((file) => {
                            const imageRef = storageRef(storage, `meals/${id || Date.now()}/${Date.now()}_${file.name}`)
                            return uploadBytes(imageRef, file).then((snapshot) => getDownloadURL(snapshot.ref))
                          })

                          try {
                            const urls = await Promise.all(promises)
                            setFormData((prev) => ({
                              ...prev,
                              meals: {
                                ...prev.meals,
                                images: [...prev.meals.images, ...urls],
                              },
                            }))
                          } catch (error) {
                            console.error("Error uploading meal images:", error)
                            setError("Failed to upload meal images")
                          }
                        }}
                      />

                      <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {formData.meals.images.map((image, index) => (
                          <div key={index} className="relative aspect-[3/4]">
                            <img
                              src={image || "/placeholder.svg"}
                              alt={`Menu ${index + 1}`}
                              className="absolute inset-0 w-full h-full object-cover rounded"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute -top-2 -right-2 rounded-full h-6 w-6 p-0 z-10"
                              onClick={() => {
                                setFormData((prev) => ({
                                  ...prev,
                                  meals: {
                                    ...prev.meals,
                                    images: prev.meals.images.filter((_, i) => i !== index),
                                  },
                                }))
                              }}
                            >
                              ×
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Facilities */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Facilities</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[400px] overflow-y-auto p-4 border rounded-lg">
                {facilityOptions.map((facility) => {
                  const isSelected = formData.facilities.some(f =>
                    typeof f === 'string' ? f === facility.id : f.id === facility.id
                  )
                  const facilityData = formData.facilities.find(f =>
                    typeof f === 'string' ? f === facility.id : f.id === facility.id
                  )
                  const isPaid = facilityData && typeof facilityData === 'object' ? facilityData.isPaid : false

                  return (
                  <div
                    key={facility.id}
                    className={`p-3 rounded-lg border ${isSelected ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}
                  >
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name={facility.id}
                        checked={isSelected}
                        onChange={handleChange}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                          <facility.icon />
                        </div>
                        <span className="text-sm">{facility.label}</span>
                      </div>
                    </label>
                    {isSelected && (
                      <label className="flex items-center space-x-2 mt-2 ml-7 cursor-pointer">
                        <input
                          type="checkbox"
                          name={`paid_${facility.id}`}
                          checked={isPaid}
                          onChange={handleChange}
                          className="w-3.5 h-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-xs text-green-600 font-medium">Paid Service (₹)</span>
                      </label>
                    )}
                  </div>
                  )
                })}
                ))}
              </div>
            </div>

            {/* Location Details */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Location Details</h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="googleMapsLink">Google Maps Link</Label>
                  <div className="flex gap-2">
                    <Input
                      id="googleMapsLink"
                      name="googleMapsLink"
                      type="url"
                      value={formData.googleMapsLink}
                      onChange={handleChange}
                      placeholder="e.g., https://maps.app.goo.gl/..."
                      className="flex-1"
                    />
                    <a
                      href="https://www.google.com/maps"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Open Maps
                    </a>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Paste a Google Maps link or enter coordinates manually below
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="latitude">Latitude</Label>
                    <Input
                      id="latitude"
                      type="number"
                      step="any"
                      value={formData.location?.lat || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        location: {
                          ...prev.location,
                          lat: parseFloat(e.target.value)
                        }
                      }))}
                      placeholder="e.g., 19.0760"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <Label htmlFor="longitude">Longitude</Label>
                    <Input
                      id="longitude"
                      type="number"
                      step="any"
                      value={formData.location?.lng || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        location: {
                          ...prev.location,
                          lng: parseFloat(e.target.value)
                        }
                      }))}
                      placeholder="e.g., 72.8777"
                      className="w-full"
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  You can enter coordinates manually if Google Maps link extraction fails. 
                  Find coordinates on Google Maps by right-clicking a location and selecting "What's here?"
                </p>
              </div>
            </div>

            {/* About The Location */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">About The Location</h2>

              <div>
                <Label>Location Highlights</Label>
                {formData.locationHighlights.map((highlight, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      value={highlight}
                      onChange={(e) => handleLocationHighlight(index, e.target.value)}
                      placeholder="Add a location highlight"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => removeLocationHighlight(index)}
                    >
                      ×
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addLocationHighlight} className="mt-2">
                  Add Highlight
                </Button>
              </div>

              <div>
                <Label htmlFor="locationImages">Location Images</Label>
                <Input
                  id="locationImages"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleLocationImagesUpload}
                />
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {formData.locationImages?.map((image, index) => (
                    <div key={index} className="relative aspect-square">
                      <img
                        src={image || "/placeholder.svg"}
                        alt={`Location ${index + 1}`}
                        className="absolute inset-0 w-full h-full object-cover rounded"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute -top-2 -right-2 rounded-full h-6 w-6 p-0 z-10"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            locationImages: prev.locationImages.filter((_, i) => i !== index),
                          }))
                        }}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Transportation */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Transportation</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="trainDistance">Distance to Nearest Train Station (km)</Label>
                  <Input
                    id="trainDistance"
                    name="trainDistance"
                    type="number"
                    step="0.1"
                    value={formData.trainDistance}
                    onChange={handleChange}
                    placeholder="e.g., 2.5"
                  />
                </div>
                <div>
                  <Label htmlFor="busDistance">Distance to Nearest Bus Stop (km)</Label>
                  <Input
                    id="busDistance"
                    name="busDistance"
                    type="number"
                    step="0.1"
                    value={formData.busDistance}
                    onChange={handleChange}
                    placeholder="e.g., 1.2"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="stationPickup"
                      name="stationPickup"
                      checked={formData.stationPickup}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="stationPickup">Station Pickup/Drop Service Available</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="busStopPickup"
                      name="busStopPickup"
                      checked={formData.busStopPickup}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label htmlFor="busStopPickup">Bus Stop Pickup/Drop Service Available</Label>
                  </div>
                </div>
              </div>
            </div>

            {/* Villa Rules */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Villa Rules</h2>
              <div>
                <Label htmlFor="rules">Rules</Label>
                <Textarea
                  id="rules"
                  name="rules"
                  value={formData.rules ? formData.rules.join("\n") : ""}
                  onChange={(e) => {
                    const rules = e.target.value.split("\n").filter((item) => item.trim() !== "")
                    setFormData((prevData) => ({
                      ...prevData,
                      rules: rules,
                    }))
                  }}
                  placeholder="Enter each rule on a new line"
                  rows={4}
                />
              </div>
            </div>

            {/* Cancellation Policy */}
<div className="space-y-4">
  <h2 className="text-xl font-semibold">Cancellation Policy</h2>
  <div>
    <Label htmlFor="cancellationPolicy">Cancellation Policy</Label>
    <Textarea
      id="cancellationPolicy"
      name="cancellationPolicy"
      value={formData.cancellationPolicy || ""}
      onChange={handleChange}
      rows={4}
      placeholder="Describe your cancellation policy (optional)"
    />
    <p className="text-sm text-gray-500 mt-1">
      Leave blank if not applicable. This section will only appear on the villa detail page if filled.
    </p>
  </div>
</div>

            {/* Management */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Management</h2>
              {formData.management.map((member, index) => (
                <div key={index} className="space-y-2 p-4 border rounded">
                  <div>
                    <Label htmlFor={`management-${index}-name`}>Name</Label>
                    <Input
                      id={`management-${index}-name`}
                      value={member.name}
                      onChange={(e) => handleManagementChange(index, "name", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`management-${index}-image`}>Image</Label>
                    <Input
                      id={`management-${index}-image`}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, `management-${index}`)}
                    />
                    {member.image && (
                      <div className="relative mt-2">
                        <img
                          src={member.image || "/placeholder.svg"}
                          alt={member.name}
                          className="mt-2 h-24 w-24 object-cover rounded"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute -top-2 -right-2 rounded-full h-6 w-6 p-0"
                          onClick={() => deleteImage(`management-${index}`)}
                        >
                          ×
                        </Button>
                      </div>
                    )}
                  </div>
                  {index > 0 && (
                    <Button type="button" variant="destructive" onClick={() => removeManagementMember(index)}>
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addManagementMember}>
                Add Management Member
              </Button>
            </div>

            {/* Owner Information */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Owner Information</h2>
              <div>
                <Label htmlFor="owner-name">Owner Name</Label>
                <Input
                  id="owner-name"
                  value={formData.owner.name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      owner: { ...prev.owner, name: e.target.value },
                    }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="ownerEmail">Owner Email</Label>
                <Input
                  id="ownerEmail"
                  name="ownerEmail"
                  type="email"
                  value={formData.ownerEmail}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="upiId">UPI ID</Label>
                <Input
                  id="upiId"
                  name="upiId"
                  value={formData.upiId}
                  onChange={handleChange}
                  placeholder="e.g., yourname@upi"
                />
              </div>
              <div>
                <Label htmlFor="owner-image">Owner Image</Label>
                <Input id="owner-image" type="file" accept="image/*" onChange={(e) => handleImageUpload(e, "owner")} />
                {formData.owner.image && (
                  <div className="relative mt-2">
                    <img
                      src={formData.owner.image || "/placeholder.svg"}
                      alt="Owner"
                      className="mt-2 h-24 w-24 object-cover rounded"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 rounded-full h-6 w-6 p-0"
                      onClick={() => deleteImage("owner")}
                    >
                      ×
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Social Links */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Social Links</h2>
              <div>
                <Label htmlFor="youtubeLink">YouTube Link</Label>
                <Input id="youtubeLink" name="youtubeLink" value={formData.youtubeLink} onChange={handleChange} />
              </div>
              <div>
                <Label htmlFor="instagramProfile">Instagram Profile</Label>
                <Input
                  id="instagramProfile"
                  name="instagramProfile"
                  value={formData.instagramProfile}
                  onChange={handleChange}
                />
              </div>
              <div>
                <Label htmlFor="whatsappNumber">WhatsApp Number</Label>
                <Input
                  id="whatsappNumber"
                  name="whatsappNumber"
                  value={formData.whatsappNumber}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* YouTube Shorts Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">YouTube Shorts</h2>

              <div>
                <Label>Featured Shorts</Label>
                {formData.youtubeShorts.map((short, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <Input
                      value={short}
                      onChange={(e) => {
                        const newShorts = [...formData.youtubeShorts]
                        newShorts[index] = e.target.value
                        setFormData((prev) => ({
                          ...prev,
                          youtubeShorts: newShorts,
                        }))
                      }}
                      placeholder="Enter YouTube Short URL or ID"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          youtubeShorts: prev.youtubeShorts.filter((_, i) => i !== index),
                        }))
                      }}
                    >
                      ×
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      youtubeShorts: [...prev.youtubeShorts, ""],
                    }))
                  }}
                  className="mt-2"
                >
                  Add Short
                </Button>
              </div>
            </div>

            {/* Check-in/Check-out Times */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Check-in/Check-out Times</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="checkInTime">Check-in Time</Label>
                  <Input
                    id="checkInTime"
                    name="checkInTime"
                    type="time"
                    value={formData.checkInTime}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="checkOutTime">Check-out Time</Label>
                  <Input
                    id="checkOutTime"
                    name="checkOutTime"
                    type="time"
                    value={formData.checkOutTime}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Adding Property..." : "Add Property"}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  )
}
